import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { UserAvatar } from '../components/AppShell';
import {
  User, Mail, Building2, Shield, Clock, Bell,
  BarChart3, FileText, Save, Loader2, Eye, Trash2,
  Calendar, DollarSign, Calculator, Plus, Briefcase, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { PasswordSecurityCard } from '../components/profile/PasswordSecurityCard';
import {
  HOURS_INCREMENT_OPTIONS,
  COST_INCREMENT_OPTIONS,
  formatHours,
  formatCost,
} from '../lib/rounding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'details', label: 'Personal Details', icon: User },
  { id: 'preferences', label: 'Preferences', icon: Settings },
  { id: 'communication', label: 'Communication', icon: Bell },
  { id: 'estimates', label: 'My Estimates', icon: Clock },
  { id: 'sows', label: 'My SOWs', icon: Briefcase },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'reports', label: 'Reports', icon: FileText },
];

const SOW_STATUS_LABELS = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  pending_cco: { label: 'Pending CCO', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  cco_approved: { label: 'CCO Approved', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cco_rejected: { label: 'CCO Rejected', className: 'bg-rose-100 text-rose-800 border-rose-200' },
  sent_to_client: { label: 'Sent to Client', className: 'bg-sky-100 text-sky-800 border-sky-200' },
  client_signed: { label: 'Client Signed', className: 'bg-purple-100 text-purple-800 border-purple-200' },
};

const SOW_FROZEN_STATUSES = new Set(['sent_to_client', 'client_signed']);

const ROLE_BADGES = {
  super_admin: { label: 'Super Admin', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  admin: { label: 'Admin', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  user: { label: 'User', className: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function ProfilePage() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');
  const [saving, setSaving] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [sows, setSows] = useState([]);
  const [loadingSows, setLoadingSows] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Editable fields
  const [name, setName] = useState(user?.name || '');
  const [prefs, setPrefs] = useState({
    email_estimate_complete: true,
    email_estimate_shared: true,
    email_system_updates: false,
  });
  const [defaultRounding, setDefaultRounding] = useState(
    user?.preferences?.default_rounding || { hours_increment: 0, cost_increment: 0 },
  );
  const [defaultEntityId, setDefaultEntityId] = useState(
    user?.preferences?.default_contracting_entity_id || '',
  );
  const [defaultRegion, setDefaultRegion] = useState(
    user?.preferences?.default_region || '',
  );
  const [seertechEntities, setSeertechEntities] = useState([]);
  const [defaultCurrencyCode, setDefaultCurrencyCode] = useState(
    user?.preferences?.default_currency_code || '',
  );
  const [defaultIncludeRawAppendix, setDefaultIncludeRawAppendix] = useState(
    Boolean(user?.preferences?.default_include_raw_appendix),
  );
  const [defaultGanttCollapsed, setDefaultGanttCollapsed] = useState(
    Boolean(user?.preferences?.default_gantt_milestones_collapsed),
  );
  // Pinned-deal push toasts (v260.58) — leadership can mute the
  // PinnedStatusNotifier or restrict it to deal-closing events.
  const [statusPushFilter, setStatusPushFilter] = useState(
    (user?.preferences?.status_push_filter || 'all'),
  );
  const [currencies, setCurrencies] = useState([]);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [applyingEntityToSows, setApplyingEntityToSows] = useState(false);

  // Re-sync when user object changes (e.g. after /auth/me refresh)
  useEffect(() => {
    if (user?.preferences?.default_rounding) {
      setDefaultRounding({
        hours_increment: Number(user.preferences.default_rounding.hours_increment || 0),
        cost_increment: Number(user.preferences.default_rounding.cost_increment || 0),
      });
    }
    setDefaultEntityId(user?.preferences?.default_contracting_entity_id || '');
    setDefaultRegion(user?.preferences?.default_region || '');
    setDefaultCurrencyCode(user?.preferences?.default_currency_code || '');
    setDefaultIncludeRawAppendix(Boolean(user?.preferences?.default_include_raw_appendix));
    setDefaultGanttCollapsed(Boolean(user?.preferences?.default_gantt_milestones_collapsed));
    setStatusPushFilter(user?.preferences?.status_push_filter || 'all');
  }, [user]);

  // Per-field dirty flags — compared against the persisted user.preferences.
  const savedRoundingHours = Number(user?.preferences?.default_rounding?.hours_increment || 0);
  const savedRoundingCost = Number(user?.preferences?.default_rounding?.cost_increment || 0);
  const savedEntityId = user?.preferences?.default_contracting_entity_id || '';
  const savedRegion = user?.preferences?.default_region || '';
  const savedCurrencyCode = user?.preferences?.default_currency_code || '';
  const savedIncludeRawAppendix = Boolean(user?.preferences?.default_include_raw_appendix);
  const savedGanttCollapsed = Boolean(user?.preferences?.default_gantt_milestones_collapsed);
  const savedStatusPushFilter = user?.preferences?.status_push_filter || 'all';
  const roundingDirty =
    Number(defaultRounding.hours_increment || 0) !== savedRoundingHours
    || Number(defaultRounding.cost_increment || 0) !== savedRoundingCost;
  const entityDirty = (defaultEntityId || '') !== savedEntityId;
  const regionDirty = (defaultRegion || '') !== savedRegion;
  const currencyDirty = (defaultCurrencyCode || '') !== savedCurrencyCode;
  const rawApDirty = defaultIncludeRawAppendix !== savedIncludeRawAppendix;
  const ganttCollapsedDirty = defaultGanttCollapsed !== savedGanttCollapsed;
  const statusPushDirty = statusPushFilter !== savedStatusPushFilter;
  const preferencesDirty = roundingDirty || entityDirty || regionDirty || currencyDirty || rawApDirty || ganttCollapsedDirty || statusPushDirty;

  const discardPreferenceChanges = () => {
    setDefaultRounding({
      hours_increment: savedRoundingHours,
      cost_increment: savedRoundingCost,
    });
    setDefaultEntityId(savedEntityId);
    setDefaultRegion(savedRegion);
    setDefaultCurrencyCode(savedCurrencyCode);
    setDefaultIncludeRawAppendix(savedIncludeRawAppendix);
    setDefaultGanttCollapsed(savedGanttCollapsed);
    setStatusPushFilter(savedStatusPushFilter);
  };

  // Load available currencies for the default-currency dropdown.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/settings/currencies`, { withCredentials: true })
      .then(res => { if (!cancelled) setCurrencies(res.data?.currencies || []); })
      .catch(() => { if (!cancelled) setCurrencies([]); });
    return () => { cancelled = true; };
  }, []);

  // Load Seertech entities for the default-entity dropdown.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/settings/seertech-entities`, { withCredentials: true })
      .then(res => { if (!cancelled) setSeertechEntities(res.data?.entities || []); })
      .catch(() => { /* silent — fall back to empty list */ });
    return () => { cancelled = true; };
  }, []);

  const savePreferences = async () => {
    setSavingPreferences(true);
    const previousEntityId = user?.preferences?.default_contracting_entity_id || '';
    const newEntityId = defaultEntityId || '';
    const entityChanged = previousEntityId !== newEntityId;
    try {
      const res = await axios.put(
        `${API_URL}/api/auth/preferences`,
        {
          default_rounding: defaultRounding,
          default_contracting_entity_id: defaultEntityId || null,
          default_region: defaultRegion || null,
          default_currency_code: defaultCurrencyCode || null,
          default_include_raw_appendix: defaultIncludeRawAppendix,
          default_gantt_milestones_collapsed: defaultGanttCollapsed,
          status_push_filter: statusPushFilter,
        },
        { withCredentials: true },
      );
      setUser(prev => ({
        ...prev,
        preferences: res.data?.preferences || prev?.preferences,
      }));
      toast.success('Preferences saved');

      // When the user JUST switched to a new non-empty default entity,
      // proactively surface how many of their draft/editable SOWs don't
      // match yet — with a one-click action to apply the bulk-sync.
      if (entityChanged && newEntityId) {
        checkEntityDriftAndNudge();
      }
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const checkEntityDriftAndNudge = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/auth/preferences/entity-drift-count`,
        { withCredentials: true },
      );
      const count = Number(res.data?.count || 0);
      if (count <= 0) return;
      toast.message(
        `${count} of your draft SOW${count === 1 ? "" : "s"} don't match your new default entity`,
        {
          description: 'Apply now to keep them in sync — sent & signed SOWs will not be touched.',
          duration: 10000,
          action: {
            label: 'Apply now',
            onClick: () => applyEntityToDraftSOWs(),
          },
          id: 'entity-drift-nudge',
        },
      );
    } catch {
      /* silent — nudge is best-effort */
    }
  };

  const applyRoundingToAllEstimates = async () => {
    const pref = user?.preferences?.default_rounding || {};
    if ((pref.hours_increment || 0) === 0 && (pref.cost_increment || 0) === 0) {
      toast.error('Save a non-zero default rounding first, then apply.');
      return;
    }
    const msg = [
      `Apply your default rounding to every estimate you own that`,
      `currently has no rounding set.\n\nPer-estimate overrides stay intact —`,
      `only estimates with rounding OFF will change.\n\nProceed?`,
    ].join(' ');
    if (!window.confirm(msg)) return;
    setApplyingToAll(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/preferences/apply-to-estimates`,
        {},
        { withCredentials: true },
      );
      const updated = res.data?.updated || 0;
      const matched = res.data?.matched || 0;
      if (updated === 0) {
        toast.info('No estimates needed updating — every owned estimate already has rounding set.');
      } else {
        toast.success(`Updated ${updated} estimate${updated === 1 ? '' : 's'} (${matched} matched).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply rounding');
    } finally {
      setApplyingToAll(false);
    }
  };

  const applyEntityToDraftSOWs = async () => {
    const entityId = user?.preferences?.default_contracting_entity_id;
    if (!entityId) {
      toast.error('Save a Default Seertech Contracting Entity first, then apply.');
      return;
    }
    const entityLabel =
      seertechEntities.find(e => e.id === entityId)?.code
      || seertechEntities.find(e => e.id === entityId)?.name
      || entityId;
    const msg = [
      `Apply "${entityLabel}" to every SOW you own that is still editable`,
      `(draft / pending CCO / CCO approved / CCO rejected).\n\n`,
      `Sent and client-signed SOWs are legally binding — they will NOT be changed.\n\nProceed?`,
    ].join(' ');
    if (!window.confirm(msg)) return;
    setApplyingEntityToSows(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/preferences/apply-entity-to-sows`,
        {},
        { withCredentials: true },
      );
      const updated = res.data?.updated || 0;
      const matched = res.data?.matched || 0;
      if (res.data?.error === 'entity_no_longer_exists') {
        toast.error('Your saved default entity no longer exists. Pick a new one and save.');
        return;
      }
      if (updated === 0) {
        toast.info('No draft SOWs needed updating.');
      } else {
        toast.success(`Updated ${updated} SOW${updated === 1 ? '' : 's'} (${matched} matched).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply entity to SOWs');
    } finally {
      setApplyingEntityToSows(false);
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TABS.find(t => t.id === tab)) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'estimates') fetchEstimates();
    if (activeTab === 'sows') fetchSows();
  }, [activeTab]);

  const fetchEstimates = async () => {
    setLoadingEstimates(true);
    try {
      const res = await axios.get(`${API_URL}/api/estimates`, { withCredentials: true });
      setEstimates(res.data?.estimates || res.data || []);
    } catch {
      toast.error('Failed to load estimates');
    } finally {
      setLoadingEstimates(false);
    }
  };

  const fetchSows = async () => {
    setLoadingSows(true);
    try {
      const res = await axios.get(`${API_URL}/api/sow?limit=100`, { withCredentials: true });
      setSows(res.data?.sows || []);
    } catch (e) {
      // 403 for roles without SOW visibility — render an empty state, no toast spam.
      if (e?.response?.status !== 403) toast.error('Failed to load SOWs');
      setSows([]);
    } finally {
      setLoadingSows(false);
    }
  };

  const deleteEstimate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/estimates/${id}`, { withCredentials: true });
      setEstimates(prev => prev.filter(est => est.id !== id));
      toast.success('Estimate deleted');
    } catch {
      toast.error('Failed to delete estimate');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getComplexityColor = (label) => {
    switch (label) {
      case 'Simple': return 'bg-green-100 text-green-700';
      case 'Moderate': return 'bg-blue-100 text-blue-700';
      case 'Complex': return 'bg-amber-100 text-amber-700';
      case 'Very Complex': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, { name: name.trim() }, { withCredentials: true });
      setUser(prev => ({ ...prev, name: name.trim() }));
      toast.success('Name updated');
    } catch {
      toast.error('Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  const roleBadge = ROLE_BADGES[user?.role] || ROLE_BADGES.user;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Profile header */}
      <div className="flex items-center gap-4 mb-8">
        <UserAvatar user={user} size="lg" />
        <div>
          <h1 className="text-xl font-bold text-[#0F2040]">{user?.name}</h1>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <Badge variant="outline" className={`mt-1 text-[10px] ${roleBadge.className}`}>
            {roleBadge.label}
          </Badge>
        </div>
      </div>

      {/* Tabs + Content */}
      <div className="flex gap-6">
        {/* Sidebar tabs */}
        <nav className="w-48 shrink-0 space-y-1" data-testid="profile-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#0F2040] text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`profile-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">

          {/* ── PERSONAL DETAILS ──────────────────────────────── */}
          {activeTab === 'details' && (
            <div className="space-y-4" data-testid="profile-details">
              <PasswordSecurityCard user={user} onChanged={() => checkAuth?.()} />
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Personal Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Full Name</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="text-sm"
                          data-testid="profile-name-input"
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveName}
                          disabled={saving || name === user?.name}
                          className="bg-[#0F2040] hover:bg-[#0F2040]/90"
                          data-testid="profile-save-name"
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Email</Label>
                      <Input value={user?.email || ''} disabled className="mt-1 text-sm bg-slate-50" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Role</Label>
                      <div className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5" />
                        {roleBadge.label}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Organization</Label>
                      <div className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600 flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5" />
                        {user?.organization_name || 'Not assigned'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Account Type</Label>
                      <div className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600">
                        {user?.auth_type === 'google' ? 'Google SSO' : 'Email / Password'}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Member Since</Label>
                      <div className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── PREFERENCES ───────────────────────────────────── */}
          {activeTab === 'preferences' && (
            <div
              className={`space-y-4 ${preferencesDirty ? 'pb-24' : ''}`}
              data-testid="profile-preferences"
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-[#4ABFCF]" />
                    Default Display Rounding
                    {roundingDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-rounding-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Pre-fills the Display Rounding on every new estimate you
                    create. Existing estimates keep whatever they were saved
                    with. Applies to Estimator display and SOW / PDF / PPT
                    exports — underlying hours and cost are always stored
                    precisely.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1.5 block">Hours Rounding</Label>
                      <select
                        value={defaultRounding.hours_increment}
                        onChange={e => setDefaultRounding(r => ({
                          ...r,
                          hours_increment: parseFloat(e.target.value) || 0,
                        }))}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABFCF]"
                        data-testid="profile-default-rounding-hours"
                      >
                        {HOURS_INCREMENT_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1.5 block">Cost Rounding</Label>
                      <select
                        value={defaultRounding.cost_increment}
                        onChange={e => setDefaultRounding(r => ({
                          ...r,
                          cost_increment: parseFloat(e.target.value) || 0,
                        }))}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABFCF]"
                        data-testid="profile-default-rounding-cost"
                      >
                        {COST_INCREMENT_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(defaultRounding.hours_increment > 0 || defaultRounding.cost_increment > 0) ? (
                    <div className="rounded-md bg-[#4ABFCF]/5 border border-[#4ABFCF]/20 px-3 py-2 text-xs text-[#0F2040]"
                         data-testid="profile-default-rounding-preview">
                      New estimates will pre-fill with&nbsp;
                      <strong>1,800 hrs → {formatHours(1800, defaultRounding)} hrs</strong> &middot;&nbsp;
                      <strong>$250,000 → {formatCost(250000, defaultRounding)}</strong>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">
                      Both set to Off — new estimates start with no rounding applied.
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-3 flex-wrap">
                    <Button
                      type="button"
                      onClick={applyRoundingToAllEstimates}
                      disabled={applyingToAll
                        || ((user?.preferences?.default_rounding?.hours_increment || 0) === 0
                            && (user?.preferences?.default_rounding?.cost_increment || 0) === 0)}
                      size="sm"
                      variant="outline"
                      className="text-[11px] h-8"
                      data-testid="profile-apply-rounding-to-all"
                    >
                      {applyingToAll
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        : <Calculator className="w-3.5 h-3.5 mr-1.5" />}
                      Apply to all my estimates
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#4ABFCF]" />
                    Default Seertech Contracting Entity
                    {entityDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-entity-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Pre-selects the Seertech entity on every SOW you create
                    (US / AU / EMEA / …). Per-SOW selection always wins —
                    this is just a time-saver for new SOWs.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block">Default Entity</Label>
                    <select
                      value={defaultEntityId || ''}
                      onChange={e => setDefaultEntityId(e.target.value)}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABFCF]"
                      data-testid="profile-default-entity-select"
                    >
                      <option value="">Use system default</option>
                      {seertechEntities.map(e => (
                        <option key={e.id} value={e.id}
                                data-testid={`profile-default-entity-option-${e.id}`}>
                          {e.code ? `${e.code} — ${e.name}` : e.name}
                        </option>
                      ))}
                    </select>
                    {seertechEntities.length === 0 && (
                      <p className="text-[10px] text-slate-400 mt-1">
                        No entities loaded — Super Admins configure them under
                        Admin → Seertech Entities.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block">Home Region</Label>
                    <select
                      value={defaultRegion || ''}
                      onChange={e => setDefaultRegion(e.target.value)}
                      disabled={!defaultEntityId}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABFCF] disabled:opacity-50"
                      data-testid="profile-default-region-select"
                    >
                      <option value="">No default region</option>
                      {(() => {
                        const en = seertechEntities.find(x => x.id === defaultEntityId);
                        const regs = en ? (en.regions?.length ? en.regions : (en.region ? [en.region] : [])) : [];
                        return regs.map(r => <option key={r} value={r}>{r}</option>);
                      })()}
                    </select>
                    <p className="text-[10px] text-slate-400 mt-1">
                      New estimates auto-fill your home entity and region (still changeable per estimate via Project Conditions).
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 flex-wrap">
                    <Button
                      onClick={applyEntityToDraftSOWs}
                      disabled={
                        applyingEntityToSows
                        || !user?.preferences?.default_contracting_entity_id
                      }
                      size="sm"
                      variant="outline"
                      className="text-[11px] h-8"
                      data-testid="profile-apply-entity-to-sows"
                    >
                      {applyingEntityToSows
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        : <Briefcase className="w-3.5 h-3.5 mr-1.5" />}
                      Apply to my draft SOWs
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#4ABFCF]" />
                    Default Currency
                    {currencyDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-currency-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Applied to new estimates / quotes / SOWs when their
                    organization has no currency set. Organizations
                    override this; individual estimates can override
                    again. Display-only — calculations stay in USD.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block">Default Currency</Label>
                    <select
                      value={defaultCurrencyCode || ''}
                      onChange={e => setDefaultCurrencyCode(e.target.value)}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABFCF]"
                      data-testid="profile-default-currency-select"
                    >
                      <option value="">Use system default</option>
                      {currencies.map(c => (
                        <option key={c.code} value={c.code}
                                data-testid={`profile-default-currency-option-${c.code}`}>
                          {c.code} — {c.name}{c.symbol ? ` (${c.symbol})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Export defaults — per-user default for the Quote export
                  "Include raw values appendix" checkbox. Org-level default
                  lives in Admin → Organizations and is used when this is
                  unset; this setting takes precedence for this user. */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#4ABFCF]" />
                    Export Defaults
                    {rawApDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-export-defaults-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Pre-fill the Quote export options for every estimate
                    you open. You can still un-check per export.
                  </p>
                </CardHeader>
                <CardContent>
                  <label
                    className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 p-3 cursor-pointer hover:bg-slate-50"
                    htmlFor="profile-default-raw-appendix"
                  >
                    <input
                      type="checkbox"
                      id="profile-default-raw-appendix"
                      data-testid="profile-default-raw-appendix"
                      checked={defaultIncludeRawAppendix}
                      onChange={e => setDefaultIncludeRawAppendix(e.target.checked)}
                      className="mt-0.5 accent-[#0F2040] cursor-pointer"
                    />
                    <span className="text-xs leading-snug">
                      <span className="font-semibold text-[#0F2040] block">
                        Include raw values appendix by default
                      </span>
                      <span className="text-slate-500 block mt-0.5">
                        Every Quote export you generate (PDF / DOCX / XLSX /
                        PPTX) will pre-check the &quot;Include raw values
                        appendix&quot; option. Useful if you regularly
                        present to boards that ask for the precise numbers.
                      </span>
                    </span>
                  </label>
                </CardContent>
              </Card>

              {/* Planner defaults — per-user default for the Gantt
                  "Collapse milestones" toggle. Exports always render
                  the full milestone set; this affects the on-screen
                  chart only. Org-level default lives on the
                  Organization record and is the fallback when this
                  user-level setting has not been opted into. */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#4ABFCF]" />
                    Planner Defaults
                    {ganttCollapsedDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-planner-defaults-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Pre-configure how the Gantt chart looks when you open
                    any estimate. Only affects on-screen; exports always
                    show the full milestone set.
                  </p>
                </CardHeader>
                <CardContent>
                  <label
                    className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 p-3 cursor-pointer hover:bg-slate-50"
                    htmlFor="profile-default-gantt-collapsed"
                  >
                    <input
                      type="checkbox"
                      id="profile-default-gantt-collapsed"
                      data-testid="profile-default-gantt-collapsed"
                      checked={defaultGanttCollapsed}
                      onChange={e => setDefaultGanttCollapsed(e.target.checked)}
                      className="mt-0.5 accent-[#0F2040] cursor-pointer"
                    />
                    <span className="text-xs leading-snug">
                      <span className="font-semibold text-[#0F2040] block">
                        Collapse Gantt milestones by default
                      </span>
                      <span className="text-slate-500 block mt-0.5">
                        The Gantt chart will open with only the Go-Live
                        flag visible and a &quot;+N milestones&quot; summary
                        chip. Ideal for PMs who frequently present to
                        clients and want a cleaner chart by default.
                      </span>
                    </span>
                  </label>
                </CardContent>
              </Card>

              {/* Notifications — pinned-deal push toasts (v260.58).
                  Controls the PinnedStatusNotifier behaviour. "All" is
                  the default and matches existing behaviour; "Won/Lost
                  only" surfaces only deal-closing events; "Off" stops
                  the poller entirely so nothing fires. */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="w-4 h-4 text-[#4ABFCF]" />
                    Notifications
                    {statusPushDirty && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid="profile-notifications-dirty-indicator"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Toast notifications when teammates change the status
                    of an estimate you&apos;ve pinned. The dashboard&apos;s
                    Activity feed always shows everything regardless of
                    this setting.
                  </p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    {
                      value: 'all',
                      label: 'All status changes',
                      desc: 'Every override on a pinned deal — Draft, In Review, Confirmed, Won, Lost, Archived…',
                    },
                    {
                      value: 'won_lost_only',
                      label: 'Won / Lost only',
                      desc: 'Just the deal-closing events. Best for leadership who only need the end of the funnel.',
                    },
                    {
                      value: 'off',
                      label: 'Off',
                      desc: 'No push toasts. Still check the Activity feed manually for updates.',
                    },
                  ].map(opt => {
                    const checked = statusPushFilter === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                          checked
                            ? 'border-[#4ABFCF] bg-[#4ABFCF]/5'
                            : 'border-slate-200 bg-slate-50/60 hover:bg-slate-50'
                        }`}
                        data-testid={`profile-status-push-${opt.value}`}
                      >
                        <input
                          type="radio"
                          name="status-push-filter"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setStatusPushFilter(opt.value)}
                          className="mt-0.5 accent-[#0F2040] cursor-pointer"
                        />
                        <span className="text-xs leading-snug">
                          <span className="font-semibold text-[#0F2040] block">{opt.label}</span>
                          <span className="text-slate-500 block mt-0.5">{opt.desc}</span>
                        </span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Sticky consolidated save bar — only when there are unsaved changes */}
              {preferencesDirty && (
                <div
                  className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur shadow-[0_-4px_12px_rgba(15,32,64,0.08)]"
                  data-testid="profile-preferences-sticky-savebar"
                >
                  <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                      <span className="font-medium text-[#0F2040]">
                        You have unsaved preference changes
                      </span>
                      <span className="text-slate-400 hidden sm:inline">·</span>
                      <span className="text-slate-500 hidden sm:inline">
                        {[
                          roundingDirty ? 'Rounding' : null,
                          entityDirty ? 'Entity' : null,
                          regionDirty ? 'Region' : null,
                          currencyDirty ? 'Currency' : null,
                          rawApDirty ? 'Export defaults' : null,
                          ganttCollapsedDirty ? 'Planner defaults' : null,
                          statusPushDirty ? 'Notifications' : null,
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={discardPreferenceChanges}
                        disabled={savingPreferences}
                        variant="outline"
                        size="sm"
                        className="text-xs h-8"
                        data-testid="profile-preferences-discard"
                      >
                        Discard
                      </Button>
                      <Button
                        onClick={savePreferences}
                        disabled={savingPreferences}
                        size="sm"
                        className="bg-[#0F2040] hover:bg-[#0F2040]/90 text-white text-xs h-8"
                        data-testid="profile-preferences-save-all"
                      >
                        {savingPreferences
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                          : <Save className="w-3.5 h-3.5 mr-1.5" />}
                        Save all preferences
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'communication' && (
            <div className="space-y-4" data-testid="profile-communication">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Email Notifications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {[
                    { key: 'email_estimate_complete', label: 'Estimate Complete', desc: 'Receive an email when an estimate calculation finishes.' },
                    { key: 'email_estimate_shared', label: 'Estimate Shared', desc: 'Receive an email when someone shares an estimate with you.' },
                    { key: 'email_system_updates', label: 'System Updates', desc: 'Receive product updates and new feature announcements.' },
                  ].map(pref => (
                    <div key={pref.key} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[#0F2040]">{pref.label}</p>
                        <p className="text-xs text-slate-500">{pref.desc}</p>
                      </div>
                      <Switch
                        checked={prefs[pref.key]}
                        onCheckedChange={(checked) => setPrefs(prev => ({ ...prev, [pref.key]: checked }))}
                        data-testid={`pref-${pref.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── MY ESTIMATES ──────────────────────────────────── */}
          {activeTab === 'estimates' && (
            <div className="space-y-4" data-testid="profile-estimates">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-[#0F2040]">My Estimate History</h2>
                  <Badge variant="outline" className="text-[10px]">{estimates.length} estimates</Badge>
                </div>
                <Button
                  onClick={() => navigate('/estimator/new')}
                  className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                  size="sm"
                  data-testid="profile-new-estimate-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  New Estimate
                </Button>
              </div>

              {loadingEstimates ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : estimates.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Calculator className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0F2040] mb-2">No Estimates Yet</h3>
                    <p className="text-sm text-slate-500 mb-4">Create your first implementation estimate to get started.</p>
                    <Button onClick={() => navigate('/estimator/new')} className="bg-[#4ABFCF] hover:bg-[#3AAAB9]">
                      <Plus className="w-4 h-4 mr-2" />Create Estimate
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {estimates.map((estimate) => (
                    <Card key={estimate.id} className="hover:shadow-lg transition-shadow" data-testid={`profile-estimate-card-${estimate.id}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-[#0F2040] font-['Outfit']">
                                {estimate.estimate_name}
                              </h3>
                              <Badge className={getComplexityColor(estimate.results?.complexity_label)}>
                                {estimate.results?.complexity_label || 'N/A'}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {formatDate(estimate.created_at)}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {estimate.implementation_type?.replace(/_/g, ' ')}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {estimate.mode === 'simple' ? 'Simple Mode' : 'Advanced Mode'}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <div className="flex items-center gap-1 text-slate-500 text-xs mb-0.5">
                                <Clock className="w-3.5 h-3.5" />
                                Effort
                              </div>
                              <div className="text-xl font-bold text-[#0F2040] font-['Outfit']">
                                {estimate.results?.total_effort?.toLocaleString() || 0} hrs
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center gap-1 text-slate-500 text-xs mb-0.5">
                                <DollarSign className="w-3.5 h-3.5" />
                                Cost
                              </div>
                              <div className="text-xl font-bold text-[#4ABFCF] font-['Outfit']">
                                ${estimate.results?.total_cost?.toLocaleString() || 0}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/estimator?load=${estimate.id}`)}
                                data-testid={`profile-view-estimate-${estimate.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/estimator/quote/${estimate.id}`)}
                                className="text-[#4ABFCF] hover:text-[#3AAAB9] hover:border-[#4ABFCF]/40"
                                data-testid={`profile-quote-${estimate.id}`}
                              >
                                <Briefcase className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteEstimate(estimate.id)}
                                disabled={deleting === estimate.id}
                                className="text-red-600 hover:text-red-700 hover:border-red-300"
                                data-testid={`profile-delete-estimate-${estimate.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Component Summary */}
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="flex flex-wrap gap-2">
                            {estimate.selected_components?.slice(0, 5).map((comp, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {comp.component_name} x {comp.quantity}
                              </Badge>
                            ))}
                            {estimate.selected_components?.length > 5 && (
                              <Badge variant="secondary" className="text-xs">
                                +{estimate.selected_components.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MY SOWs ──────────────────────────────────────── */}
          {activeTab === 'sows' && (
            <div className="space-y-4" data-testid="profile-sows">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-[#0F2040]">My SOWs</h2>
                  <Badge variant="outline" className="text-[10px]">{sows.length} SOWs</Badge>
                </div>
                {user?.preferences?.default_contracting_entity_id && (
                  <Button
                    onClick={applyEntityToDraftSOWs}
                    disabled={applyingEntityToSows}
                    size="sm"
                    variant="outline"
                    className="text-[11px] h-8"
                    data-testid="profile-sows-bulk-apply-entity"
                  >
                    {applyingEntityToSows
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      : <Briefcase className="w-3.5 h-3.5 mr-1.5" />}
                    Apply default entity to drafts
                  </Button>
                )}
              </div>

              {loadingSows ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : sows.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Briefcase className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0F2040] mb-2">No SOWs Yet</h3>
                    <p className="text-sm text-slate-500">
                      Create an estimate, then spin up an SOW from its Quote page.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {sows.map((sow) => {
                    const userDefault = user?.preferences?.default_contracting_entity_id;
                    const entity = seertechEntities.find(x => x.id === sow.contracting_entity_id);
                    const entityLabel = entity?.code || entity?.name || sow.contracting_entity_id || '—';
                    const isFrozen = SOW_FROZEN_STATUSES.has(sow.status);
                    // Drift only shown when the user has a default AND this SOW's entity differs.
                    const drift = (
                      userDefault
                      && sow.contracting_entity_id
                      && sow.contracting_entity_id !== userDefault
                    );
                    const statusMeta = SOW_STATUS_LABELS[sow.status]
                      || { label: sow.status || 'unknown', className: 'bg-slate-100 text-slate-700 border-slate-200' };
                    return (
                      <Card key={sow.sow_id} className="hover:shadow-md transition-shadow"
                            data-testid={`profile-sow-card-${sow.sow_id}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <h3 className="text-sm font-semibold text-[#0F2040] truncate">
                                  {sow.customer_legal_name || 'Untitled Customer'}
                                </h3>
                                <Badge variant="outline"
                                       className={`text-[10px] ${statusMeta.className}`}
                                       data-testid={`profile-sow-status-${sow.sow_id}`}>
                                  {statusMeta.label}
                                </Badge>
                                <Badge variant="outline" className="text-[10px]"
                                       data-testid={`profile-sow-entity-${sow.sow_id}`}>
                                  <Building2 className="w-3 h-3 mr-1" />{entityLabel}
                                </Badge>
                                {drift && (
                                  <Badge variant="outline"
                                         title={
                                           isFrozen
                                             ? `Entity differs from your default — this SOW is ${statusMeta.label.toLowerCase()} and cannot be bulk-updated.`
                                             : `Entity differs from your default — open the SOW to sync, or use the bulk-apply button.`
                                         }
                                         className="text-[10px] bg-amber-50 text-amber-800 border-amber-200"
                                         data-testid={`profile-sow-drift-${sow.sow_id}`}>
                                    Entity differs from default
                                  </Badge>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(sow.updated_at || sow.created_at)}
                                </span>
                                {sow.go_live_date && (
                                  <span>Go-live {sow.go_live_date}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/sow/${sow.sow_id}`)}
                                data-testid={`profile-sow-open-${sow.sow_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1.5" />Open
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── DASHBOARD ─────────────────────────────────────── */}
          {activeTab === 'dashboard' && (
            <div className="space-y-4" data-testid="profile-dashboard">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Personal Dashboard</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-slate-500">Total Estimates</p>
                      <p className="text-2xl font-bold text-[#0F2040]" data-testid="dash-total-estimates">{estimates.length || '—'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-slate-500">Avg Effort</p>
                      <p className="text-2xl font-bold text-[#0F2040]" data-testid="dash-avg-effort">
                        {estimates.length > 0
                          ? `${Math.round(estimates.reduce((s, e) => s + (e.total_effort || 0), 0) / estimates.length)}h`
                          : '—'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-slate-500">Avg Cost</p>
                      <p className="text-2xl font-bold text-[#0F2040]" data-testid="dash-avg-cost">
                        {estimates.length > 0
                          ? `$${Math.round(estimates.reduce((s, e) => s + (e.total_cost || 0), 0) / estimates.length).toLocaleString()}`
                          : '—'}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 text-center">More dashboard insights coming soon.</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── REPORTS ────────────────────────────────────────── */}
          {activeTab === 'reports' && (
            <div className="space-y-4" data-testid="profile-reports">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">My Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 text-center py-8">Report generation coming soon. You'll be able to generate summary reports across your estimates here.</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
